import type { PluginListenerHandle } from "@capacitor/core";
import { appUrlFromLaunch } from "@/native-shell/launch-url";

interface AppLinkPlugin {
  getLaunchUrl(): Promise<{ url: string } | undefined>;
  addListener(
    event: "appUrlOpen",
    listener: (event: { url: string }) => void,
  ): Promise<PluginListenerHandle>;
}

/** Subscribe before reading the cold launch so a newer link always wins. */
export function listenForNativeAppLinks(
  plugin: AppLinkPlugin,
  navigate: (url: string) => void,
  launchMemory?: Pick<Storage, "getItem" | "setItem">,
) {
  let disposed = false;
  let receivedLink = false;
  let listener: PluginListenerHandle | undefined;

  function open(url: string) {
    const target = appUrlFromLaunch(url, {
      allowDevelopmentOrigins: process.env.NODE_ENV === "development",
    });
    if (!disposed && target) navigate(target);
  }

  void (async () => {
    try {
      listener = await plugin.addListener("appUrlOpen", ({ url }) => {
        receivedLink = true;
        open(url);
      });
      if (disposed) {
        await listener.remove();
        return;
      }
      const launchKey = "peakwave.native.cold-launch-consumed";
      try {
        if (launchMemory?.getItem(launchKey) === "true") return;
      } catch {
        // Storage restrictions must not prevent ordinary link handling.
      }
      const launch = await plugin.getLaunchUrl();
      if (disposed) return;
      try {
        launchMemory?.setItem(launchKey, "true");
      } catch {
        // The current WebView can still handle this launch without storage.
      }
      if (!receivedLink && launch) open(launch.url);
    } catch {
      // Ordinary web navigation remains available if the native bridge fails.
    }
  })();

  return () => {
    disposed = true;
    void listener?.remove().catch(() => undefined);
  };
}
